/* eslint-disable react-refresh/only-export-components */
// This module intentionally co-locates the provider with its hooks (they share
// one small context). Fast-refresh's component-only rule doesn't apply.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

export interface StuckEscapeActionDef {
  /** Stable id used for registration/deregistration. */
  id: string;
  label: string;
  description?: string;
  onSelect: () => void;
}

interface StuckEscapeContextValue {
  actions: StuckEscapeActionDef[];
  register: (action: StuckEscapeActionDef) => void;
  unregister: (id: string) => void;
}

const StuckEscapeContext = createContext<StuckEscapeContextValue | null>(null);

export function StuckEscapeProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<StuckEscapeActionDef[]>([]);
  const register = useCallback((action: StuckEscapeActionDef) => {
    setActions((prev) => [...prev.filter((a) => a.id !== action.id), action]);
  }, []);
  const unregister = useCallback((id: string) => {
    setActions((prev) => prev.filter((a) => a.id !== id));
  }, []);
  return (
    <StuckEscapeContext.Provider value={{ actions, register, unregister }}>
      {children}
    </StuckEscapeContext.Provider>
  );
}

/** Read the currently-registered contextual escape actions. */
export function useStuckEscapeActions(): StuckEscapeActionDef[] {
  return useContext(StuckEscapeContext)?.actions ?? [];
}

/**
 * Register one or more contextual escape actions for the current page. They are
 * shown as the primary options in the global StuckEscapeHatch while this
 * component is mounted, and removed on unmount. onSelect always calls the latest
 * closure. Labels are assumed static (change ids to force a re-register).
 */
export function useStuckEscapeAction(actions: StuckEscapeActionDef | StuckEscapeActionDef[]): void {
  const ctx = useContext(StuckEscapeContext);
  const list = Array.isArray(actions) ? actions : [actions];
  const latest = useRef(list);
  latest.current = list;
  const ids = list.map((a) => a.id).join('|');
  // Depend on the individual register/unregister functions (stable via
  // useCallback([]) in the provider), NOT the whole context value object —
  // that object is a fresh reference every time `actions` changes, which
  // would otherwise re-run this effect on every render and infinite-loop.
  const register = ctx?.register;
  const unregister = ctx?.unregister;

  useEffect(() => {
    if (!register || !unregister) return;
    const wrappers: StuckEscapeActionDef[] = latest.current.map((a) => ({
      id: a.id,
      label: a.label,
      description: a.description,
      onSelect: () => latest.current.find((x) => x.id === a.id)?.onSelect(),
    }));
    wrappers.forEach(register);
    return () => wrappers.forEach((w) => unregister(w.id));
  }, [register, unregister, ids]);
}
